import { useFetch } from "~vue/api";
import { type UploadedFile } from "~vue/appTypes";
import { useFileUpload } from "~vue/composables/useFileUpload";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatOversizedFiles, type OversizedFile } from "~vue/utils/fileUpload";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";

type ProfileSourceItem = {
    id: string;
    file_id: number;
    name: string;
    topic: string;
    last_used_at: string | null;
};
type ProfileSourcesResponse = {
    sources: ProfileSourceItem[];
};

type CreateSourcePayload = {
    file_ids: number[];
    topic: string;
};

// Module-level singleton so linkedSources is shared across all components that use it.
// `immediate: false` avoids firing on module evaluation: the endpoint 404s by design for
// users without the PUBLIC_PROFILE flag, and the collaboration-profile consumers below
// only mount for entitled users, so the request is deferred to their onMounted.
const {
    data: linkedSources,
    execute: executeFetchLinkedSources,
    error: fetchLinkedSourcesError,
    isFetching: isFetchingLinkedSources,
} = useFetch("/v1/profile/sources", { immediate: false }).json<ProfileSourcesResponse>();

/**
 * Use `fetchOnMount: false` for read-only profiles: linked sources are needed only by the editing sidebar.
 */
export const useProfileSources = ({ fetchOnMount = true }: { fetchOnMount?: boolean } = {}) => {
    onMounted(() => {
        if (fetchOnMount && !isFetchingLinkedSources.value) {
            void fetchLinkedSources();
        }
    });
    const uploadedFiles = ref<UploadedFile[]>([]);
    const fileUploadError = ref<string>("");

    const { uploadFiles, acceptedFileTypes, isUploading } = useFileUpload({
        unsupportedFileCallback: (files: File[]) => {
            fileUploadError.value += `\nUnsupported file type: ${files.map((f) => f.name).join(", ")}`;
        },
        oversizedFileCallback: (files: OversizedFile[]) => {
            fileUploadError.value += `\nFile too large: ${formatOversizedFiles(files)}.`;
        },
        errorCallback: () => {
            fileUploadError.value += "\nFailed to upload file. Please try again.";
        },
    });

    const handleFileUpload = async (files: File[], topicName?: string): Promise<void> => {
        fileUploadError.value = "";
        const results = await uploadFiles(files);
        if (results.uploadedFiles && results.uploadedFiles.length > 0) {
            results.uploadedFiles.forEach((file: { id: number; name: string; size: number }) => {
                uploadedFiles.value.push({
                    id: file.id,
                    name: file.name,
                    size: file.size,
                    status: "ready",
                });
                void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_DOCUMENT_UPLOADED, {
                    file_size: file.size,
                    total_files: uploadedFiles.value.length,
                });
            });
        }

        // if topic name is not supplied, it will still do the file upload but not the linking.
        if (!fileUploadError.value && topicName) {
            await handleCreateSources(topicName);
        }
    };
    const removeFile = (fileId: number): void => {
        const removedFile = uploadedFiles.value.find((f) => f.id === fileId);
        uploadedFiles.value = uploadedFiles.value.filter((f) => f.id !== fileId);

        if (removedFile) {
            void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_DOCUMENT_REMOVED, {
                remaining_files: uploadedFiles.value.length,
            });
        }
    };

    const fetchLinkedSources = async (): Promise<void> => {
        fetchLinkedSourcesError.value = null;
        try {
            await executeFetchLinkedSources();
        } catch {
            fetchLinkedSourcesError.value = "Failed to fetch linked sources. Please try again.";
        }
    };

    const createSourcePayload = ref<CreateSourcePayload | null>(null);
    const {
        execute: executeCreateSource,
        isFetching: isCreatingSource,
        error: createSourceError,
    } = useFetch<CreateSourcePayload>("/v1/profile/sources", { immediate: false }).post(createSourcePayload).json<ProfileSourceItem[]>();

    const handleCreateSources = async (topic: string): Promise<void> => {
        createSourceError.value = null;
        const filesToLink = uploadedFiles.value.filter((f) => {
            return !linkedSources.value?.sources.some((source) => source.file_id === f.id && source.topic === topic);
        });
        if (filesToLink.length === 0) {
            createSourceError.value = "No new files to link for the specified topic.";
            return;
        }
        createSourcePayload.value = { file_ids: [...new Set(filesToLink.map((f) => f.id))], topic };
        try {
            await executeCreateSource();
            await fetchLinkedSources();
        } catch {
            createSourceError.value = "Failed to link source. Please try again.";
        }
    };

    const deleteSourceId = ref<string | null>(null);
    const deleteUrl = computed(() => (deleteSourceId.value ? `/v1/profile/sources/${deleteSourceId.value}` : ""));
    const { execute: executeDeleteSource, isFetching: isDeletingSource, error: deleteSourceError } = useFetch(deleteUrl, { immediate: false }).delete();

    const handleDeleteSource = async (sourceId: string): Promise<void> => {
        deleteSourceError.value = null;
        if (!sourceId) {
            deleteSourceError.value = "Invalid source ID. Please try again.";
            return;
        }
        deleteSourceId.value = sourceId;
        try {
            await executeDeleteSource();
            const toDeleteSource = linkedSources.value?.sources.find((s) => s.id === sourceId);
            if (toDeleteSource) {
                uploadedFiles.value = uploadedFiles.value.filter((f) => f.id !== toDeleteSource.file_id);
            }
            await fetchLinkedSources();
        } catch {
            deleteSourceError.value = "Failed to unlink source. Please try again.";
        }
    };

    return {
        uploadedFiles,
        removeFile,
        handleFileUpload,
        fileUploadError,
        fetchLinkedSourcesError,
        acceptedFileTypes,
        isUploading,
        // sources
        isFetchingLinkedSources,
        linkedSources,
        fetchLinkedSources,
        handleCreateSources,
        isCreatingSource,
        createSourceError,
        handleDeleteSource,
        isDeletingSource,
        deleteSourceError,
    };
};
